<script>
import {PerfectScrollbar} from 'vue3-perfect-scrollbar';
import AkButtonActionV2 from '@components/v2/general/AkButtonActionV2.vue';
import AkButtonValidation from '@components/v2/general/AkButtonValidation.vue';

/* FILTERS */

export default {
  name: 'GaFilterSidebar',
  components: {
    AkButtonValidation,
    AkButtonActionV2,
    PerfectScrollbar,
  },
  props: {
    filterComponents: {
      type: Array,
      required: true,
    },
    filter: {
      type: Object,
      required: true,
    },
  },
  emits: ['apply', 'reset', 'update:filter', 'cancel'],
  data() {
    return {
      isVisible: false,
      expandedFilterProperty: null,
      currentFilter: null,
      loaded: false,
      canGoBack: false,
    };
  },
  mounted() {
    this.currentFilter = {...this.filterValue};
  },
  methods: {
    show() {
      document.body.style.overflow = 'hidden';
      this.canGoBack = this.filterComponents.length > 1;
      this.expandedFilterProperty = null;
      this.currentFilter = {...this.filterValue};
      if (this.filterComponents.length === 1) this.expandedFilterProperty = this.filterComponents[0].filterProperty;

      const snapshotAll = () => {
        this.filterComponents.forEach((f) => {
          const ref = this.$refs[`filter_${f.filterProperty}`];
          if (!ref?.[0]?.canGoBack && ref?.[0]?.resetCurrentFilter) ref[0].resetCurrentFilter();
          if (ref?.[0]?.snapshotFilterState) ref[0].snapshotFilterState();
        });
      };

      if (!this.loaded && this.filterComponents.length > 0) {
        this.$nextTick(() => {
          const initPromises = this.filterComponents.map((f) => {
            const ref = this.$refs[`filter_${f.filterProperty}`];
            if (ref?.[0]?.init) return Promise.resolve(ref[0].init());
            console.error('Missing ref for', f.filterProperty);
            return Promise.resolve();
          });
          this.loaded = true;
          Promise.all(initPromises).then(() => this.$nextTick(snapshotAll));
        });
      } else {
        this.$nextTick(snapshotAll);
      }
      this.isVisible = true;
    },
    apply() {
      this.filterComponents.forEach((f) => {
        const ref = this.$refs[`filter_${f.filterProperty}`];
        if (!ref?.[0]?.canGoBack && ref?.[0]?.applyFilterCopy) ref[0].applyFilterCopy();
      });
      this.filterValue = this.currentFilter;
      this.filterComponents.forEach((f) => {
        const ref = this.$refs[`filter_${f.filterProperty}`];
        if (ref?.[0]?.commitFilterState) ref[0].commitFilterState();
      });

      this.$emit('apply');
      this.close();
    },
    restoreAllFilterState() {
      this.filterComponents.forEach((f) => {
        const ref = this.$refs[`filter_${f.filterProperty}`];
        if (ref?.[0]?.restoreFilterState) ref[0].restoreFilterState();
      });
    },
    reset() {
      this.filterComponents.forEach((f) => {
        const ref = this.$refs[`filter_${f.filterProperty}`];
        if (ref?.[0]?.clearFilter) ref[0].clearFilter();
        else this.filterValue[f.filterProperty] = undefined;
      });
      this.filterValue = this.currentFilter;
      this.filterComponents.forEach((f) => {
        const ref = this.$refs[`filter_${f.filterProperty}`];
        if (ref?.[0]?.commitFilterState) ref[0].commitFilterState();
      });
      this.$emit('reset');
      this.close();
    },
    close() {
      this.isVisible = false;
      document.body.style.overflow = 'initial';
    },
    setExpanded(filterProperty) {
      this.expandedFilterProperty = filterProperty;
    },
    back() {
      if (this.canGoBack) this.expandedFilterProperty = null;
      else this.dismiss();
    },
    dismiss() {
      this.restoreAllFilterState();
      this.close();
      setTimeout(() => {
        this.$emit('cancel');
      }, 200); // sidebar close animation
    },
    onDissmiss(e) {
      if (e.target.id === 'GaFilterMask') this.dismiss();
    },
    applyFilter() {
      if (this.canGoBack) this.back();
      else this.apply();
    },
  },
  computed: {
    filterValue: {
      get() {
        return this.filter;
      },
      set(value) {
        this.$emit('update:filter', value);
      },
    },
    hasOptionDetail() {
      return this.expandedFilterProperty !== null;
    },
    canOpen() {
      return this.currentFilter != null;
    },
    renderedFilterComponents() {
      return this.filterComponents.filter((f) => f.component);
    },
  },
  watch: {
    filterComponents(newVal, oldVal) {
      if (oldVal.length !== newVal.length) {
        this.loaded = false;
        this.canGoBack = newVal.length > 1;
      }
    },
  },
};
</script>

<template>
  <teleport v-if="canOpen" to="body">
    <transition name="fade-in">
      <div
        v-show="isVisible"
        class="fixed inset-0 z-[10000] w-screen h-dvh bg-gray-700/20"
        id="GaFilterMask"
        @click="onDissmiss">
        <transition name="slide-in">
          <div
            v-show="isVisible"
            class="absolute flex md:rounded-none rounded-t-3xl flex-col bottom-0 right-0 h-[80vh] md:h-screen bg-white w-full md:w-1/3 md:max-w-[450px] min-w-[350px] shadow-lg shadow-grey-400/20">
            <!--  HEADER  -->
            <div
              v-if="!hasOptionDetail"
              class="h-[86px] items-center px-8 py-6 flex justify-between sticky border-grey-100 border-b">
              <span class="text-[18px] font-semibold text-grey-900">Tous les filtres</span>
              <button @click="dismiss">
                <i class="ga-icon ga-supprimer text-3xl" />
              </button>
            </div>
            <perfect-scrollbar
              :options="{suppressScrollX: true, suppressScrollY: this.hasOptionDetail}"
              class="grow min-h-0">
              <div class="flex flex-col max-h-full md:h-fit h-full">
                <component
                  v-for="f in renderedFilterComponents"
                  :visible="!hasOptionDetail || expandedFilterProperty === f.filterProperty"
                  :isExpanded="hasOptionDetail && expandedFilterProperty === f.filterProperty"
                  :key="f.filterProperty"
                  :is="f.component"
                  :name="f.filterProperty"
                  :label="f.label"
                  :ref="`filter_${f.filterProperty}`"
                  v-model="currentFilter[f.filterProperty]"
                  :hasIcon="f.showIcon"
                  :canGoBack="f.canGoBack"
                  @expand="setExpanded"
                  @apply="applyFilter"
                  @back="back" />
              </div>
            </perfect-scrollbar>
            <div v-if="!hasOptionDetail" class="border-t border-grey-100 px-8 py-6 flex gap-2">
              <AkButtonActionV2 label="Tout effacer" @click="reset" class="btn-secondary !h-[48px] font-medium w-1/2" />
              <AkButtonActionV2 label="Appliquer" @click="apply" class="btn-primary !h-[48px] font-medium w-1/2" />
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<style scoped>
.fade-in-enter-active,
.fade-in-leave-active {
  transition: opacity 0.1s ease-out;
}
.fade-in-enter-from,
.fade-in-leave-to {
  opacity: 0;
}

.slide-in-enter-active,
.slide-in-leave-active {
  transition: transform 0.3s ease;
}
.slide-in-enter-from,
.slide-in-leave-to {
  transform: translateY(100%);
}

@media (min-width: 768px) {
  .slide-in-enter-from,
  .slide-in-leave-to {
    transform: translateX(100%);
  }
}
</style>
